import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/common/ScrollToTop';
import Spinner from './components/layout/Spinner';

import LanguageWrapper from './components/common/LanguageWrapper';
import ReactGA from 'react-ga4';
import AnalyticsTracker from './components/common/AnalyticsTracker';

// Layouts
import AuthLayout from './components/auth/AuthLayout';
import MainLayout from './components/layout/MainLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import ProfileLayout from './components/layout/ProfileLayout';
import MyContentLayout from './components/layout/MyContentLayout';
import SettingsLayout from './pages/Settings/SettingsLayout';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import CreatorRoute from './components/auth/CreatorRoute';
import GuestRoute from './components/auth/GuestRoute';
import AdminRoute from './components/auth/AdminRoute';
import MercadoPagoCallback from './components/auth/MercadoPagoCallback';

const GA_MEASUREMENT_ID = 'G-L83NEDDHMJ';
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}

// --- LAZY LOADING ---
// (Otras importaciones lazy)...
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const VerifyEmailSent = lazy(() => import('./pages/Auth/VerifyEmailSent'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/Auth/VerifyEmail'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AttributesAdmin = lazy(() => import('./pages/Admin/AttributesAdmin'));
const RevenueDashboard = lazy(() => import('./pages/Admin/RevenueDashboard'));
const CategoriesAdmin = lazy(() => import('./pages/Admin/CategoriesAdmin'));
const UserManagement = lazy(() => import('./pages/Admin/UserManagement'));
const ContentManagement = lazy(() => import('./pages/Admin/ContentManagement'));
const CreatorReport = lazy(() => import('./pages/Admin/CreatorReport'));
const Referrals = lazy(() => import('./pages/Dashboard/Referrals'));
const PayoutsManagement = lazy(() => import('./pages/Admin/PayoutsManagement'));
const ReferralManagement = lazy(() =>
  import('./pages/Admin/ReferralManagement')
);
const TrendingContentReport = lazy(() =>
  import('./pages/Admin/TrendingContentReport')
);
const TopSupportersReport = lazy(() =>
  import('./pages/Admin/TopSupportersReport')
);
const DmcaClaimsAdmin = lazy(() => import('./pages/Admin/DmcaClaimsAdmin'));
const VerificationManagement = lazy(() =>
  import('./pages/Admin/VerificationManagement')
);
const Home = lazy(() => import('./pages/Home'));
const Explore = lazy(() => import('./pages/Explore'));
const NotFound = lazy(() => import('./pages/NotFound'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MyPurchases = lazy(() => import('./pages/MyPurchases'));
const MyDonations = lazy(() => import('./pages/MyDonations'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Analytics = lazy(() => import('./pages/Dashboard/Analytics'));
const PaymentSettings = lazy(() =>
  import('./components/dashboard/PaymentSettings')
);
const DonationsReceivedPage = lazy(() =>
  import('./pages/Dashboard/DonationsReceivedPage')
);
const Profile = lazy(() => import('./pages/Profile'));
const PublicProfileWrapper = lazy(() =>
  import('./pages/Profile/PublicProfileWrapper')
);
const FollowLists = lazy(() => import('./components/profile/FollowLists'));
const ProfileSettings = lazy(() =>
  import('./pages/Settings/pages/ProfileSettings')
);
const AccountSettings = lazy(() =>
  import('./pages/Settings/pages/AccountSettings')
);
const PaymentSettingsPage = lazy(() =>
  import('./pages/Settings/pages/PaymentSettingsPage')
);
const NotificationSettings = lazy(() =>
  import('./pages/Settings/pages/NotificationSettings')
);
const DangerZone = lazy(() => import('./pages/Settings/pages/DangerZone'));
const ConfirmEmailChange = lazy(() =>
  import('./pages/Settings/pages/ConfirmEmailChange')
);
const Posts = lazy(() => import('./pages/Posts'));
const PostDetail = lazy(() => import('./pages/Posts/PostDetail'));
const PostEdit = lazy(() => import('./pages/Posts/PostEdit'));
const Packs = lazy(() => import('./pages/Packs'));
const PackDetail = lazy(() => import('./pages/Packs/PackDetail'));
const PackEdit = lazy(() => import('./pages/Packs/PackEdit'));
const DonationPage = lazy(() => import('./pages/DonationPage'));
const DonationSuccess = lazy(() =>
  import('./pages/DonationResult/DonationSuccess')
);
const DonationFailure = lazy(() =>
  import('./pages/DonationResult/DonationFailure')
);
const DonationPending = lazy(() =>
  import('./pages/DonationResult/DonationPending')
);
const PurchaseSuccess = lazy(() =>
  import('./pages/PurchaseResult/PurchaseSuccess')
);
const PurchaseFailure = lazy(() =>
  import('./pages/PurchaseResult/PurchaseFailure')
);
const PurchasePending = lazy(() =>
  import('./pages/PurchaseResult/PurchasePending')
);
const PostRedirect = lazy(() => import('./components/redirects/PostRedirect'));
const PackRedirect = lazy(() => import('./components/redirects/PackRedirect'));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const DmcaPolicy = lazy(() => import('./pages/Legal/DmcaPolicy'));
const VerificationPage = lazy(() => import('./pages/Verification'));
const ForAdultCreatorsPage = lazy(() =>
  import('./pages/Landing/ForAdultCreators')
);
const BlogLayout = lazy(() => import('./components/layout/BlogLayout'));
const BlogAlternativasCafecito = lazy(() =>
  import('./pages/Blog/AlternativasCafecito')
);
const MyCourses = lazy(() => import('./pages/Courses/MyCourses'));
const CreateCourse = lazy(() => import('./pages/Courses/CreateCourse'));
const CourseEditor = lazy(() => import('./pages/Courses/CourseEditor'));
const CourseViewer = lazy(() => import('./pages/Courses/CourseViewer'));

import './App.css';

function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: { background: '#333', color: '#fff' },
          success: { duration: 3000 },
        }}
      />
      <ScrollToTop />
      <AnalyticsTracker />
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Grupo de rutas que SÍ deben tener el prefijo de idioma */}
          <Route path="/:lang/*" element={<LanguageWrapper />}>
            <Route element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="explore" element={<Explore />} />

              {/* --- INICIO DE LA CORRECCIÓN --- */}
              {/* 1. ELIMINAR LA RUTA DE AQUÍ */}
              {/* <Route path="courses/:courseId" element={<CourseViewer />} /> */}
              {/* --- FIN DE LA CORRECCIÓN --- */}

              <Route path="donate/:username" element={<DonationPage />} />
              <Route path="terms" element={<TermsOfService />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="dmca" element={<DmcaPolicy />} />
              <Route
                path="creadores/adultos"
                element={<ForAdultCreatorsPage />}
              />
              <Route path="purchase/success" element={<PurchaseSuccess />} />
              <Route path="purchase/failure" element={<PurchaseFailure />} />
              <Route path="purchase/pending" element={<PurchasePending />} />
              <Route path="blog" element={<BlogLayout />}>
                <Route
                  path="alternativas-cafecito-onlyfans"
                  element={<BlogAlternativasCafecito />}
                />
              </Route>

              {/* (Rutas de Admin)... */}
              <Route
                path="admin/*"
                element={
                  <AdminRoute>
                    <Routes>
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route
                        path="commission-report"
                        element={<RevenueDashboard />}
                      />
                      <Route
                        path="creator-report"
                        element={<CreatorReport />}
                      />
                      <Route
                        path="trending-content"
                        element={<TrendingContentReport />}
                      />
                      <Route path="payouts" element={<PayoutsManagement />} />
                      <Route
                        path="referral-report"
                        element={<ReferralManagement />}
                      />
                      <Route
                        path="top-supporters-report"
                        element={<TopSupportersReport />}
                      />
                      <Route path="categories" element={<CategoriesAdmin />} />
                      <Route path="attributes" element={<AttributesAdmin />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route
                        path="verifications"
                        element={<VerificationManagement />}
                      />
                      <Route
                        path="content-management"
                        element={<ContentManagement />}
                      />
                      <Route path="dmca-claims" element={<DmcaClaimsAdmin />} />
                    </Routes>
                  </AdminRoute>
                }
              />

              {/* (Rutas de Auth)... */}
              <Route element={<GuestRoute />}>
                <Route element={<AuthLayout />}>
                  <Route path="login" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route
                    path="verify-email-sent"
                    element={<VerifyEmailSent />}
                  />
                  <Route
                    path="auth/forgot-password"
                    element={<ForgotPassword />}
                  />
                  <Route
                    path="auth/reset-password/:token"
                    element={<ResetPassword />}
                  />
                </Route>
              </Route>

              {/* (Rutas de Perfil Público)... */}
              <Route path="u/:username/*" element={<ProfileLayout />}>
                <Route index element={<PublicProfileWrapper />} />
                <Route path="posts" element={<Posts />} />
                <Route path="posts/:postId" element={<PostDetail />} />
                <Route path="packs/:packId" element={<PackDetail />} />
                <Route path="packs" element={<Packs />} />

                {/* --- INICIO DE LA CORRECCIÓN --- */}
                {/* 2. AÑADIR LA RUTA DE CURSOS AQUÍ */}
                <Route path="courses/:courseId" element={<CourseViewer />} />
                {/* --- FIN DE LA CORRECCIÓN --- */}

                <Route
                  path="followers"
                  element={<FollowLists mode="followers" />}
                />
                <Route
                  path="following"
                  element={<FollowLists mode="following" />}
                />
              </Route>

              {/* (Rutas Protegidas Generales)... */}
              <Route element={<ProtectedRoute />}>
                <Route path="my/purchases" element={<MyPurchases />} />
                <Route path="my/donations" element={<MyDonations />} />
                <Route path="my/profile" element={<Profile />} />
                <Route path="my/profile/edit" element={<Profile />} />
                <Route path="my/verification" element={<VerificationPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="my/settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="account" element={<AccountSettings />} />
                  <Route path="payments" element={<PaymentSettingsPage />} />
                  <Route
                    path="notifications"
                    element={<NotificationSettings />}
                  />
                  <Route path="danger-zone" element={<DangerZone />} />
                </Route>
              </Route>

              {/* (Rutas Protegidas de Creador)... */}
              <Route element={<CreatorRoute />}>
                {/* Rutas de contenido existente */}
                <Route path="my" element={<MyContentLayout />}>
                  <Route index element={<Navigate to="posts" replace />} />
                  <Route path="posts/*" element={<Posts />} />
                  <Route path="packs/*" element={<Packs />} />
                  <Route path="courses" element={<MyCourses />} />
                </Route>
                <Route path="my/posts/:postId/edit" element={<PostEdit />} />
                <Route path="my/packs/:packId/edit" element={<PackEdit />} />
                <Route path="my/courses/create" element={<CreateCourse />} />
                <Route
                  path="my/courses/edit/:courseId"
                  element={<CourseEditor />}
                />
                {/* Rutas de dashboard existente */}
                <Route path="dashboard" element={<DashboardLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="posts/*" element={<Posts />} />{' '}
                  <Route path="packs/*" element={<Packs />} />{' '}
                  <Route path="donations" element={<DonationsReceivedPage />} />
                  <Route path="settings" element={<PaymentSettings />} />
                  <Route path="referrals" element={<Referrals />} />
                  <Route path="courses" element={<MyCourses />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>

          {/* Rutas que NO deben tener prefijo de idioma */}
          {/* (Otras rutas sin prefijo)... */}
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/auth/mp/callback" element={<MercadoPagoCallback />} />
          <Route path="/donation/success" element={<DonationSuccess />} />
          <Route path="/donation/failure" element={<DonationFailure />} />
          <Route path="/donation/pending" element={<DonationPending />} />
          <Route
            path="/my/settings/confirm-email-change/:token"
            element={<ConfirmEmailChange />}
          />
          <Route
            path="/profile/:username"
            element={<Navigate to="/es/u/:username" replace />}
          />
          <Route path="/posts/:postId" element={<PostRedirect />} />
          <Route path="/packs/:packId" element={<PackRedirect />} />
          <Route path="/sitemap.xml" element={null} />

          <Route path="/*" element={<LanguageWrapper />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
